import { FC } from "react";
import cn from "classnames";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Tab,
  TabGroup,
  TabList,
} from "@headlessui/react";
import { CaretDownLine, CheckMarkThin } from "@/icons";
import { useTabContext } from "@/components/TabPanels/TabContext";

const THEME_STYLES = {
  black: {
    tabUnderline: "bg-swdc-orange-400",
    buttonMobile: "text-swdc-white bg-swdc-gray-450",
    tabMobile: "bg-swdc-gray-500 text-swdc-white hover:bg-swdc-gray-450",
    tabMobileActive:
      "data-selected:bg-swdc-cool-gray-600 data-selected:hover:border-swdc-cool-gray-600 data-selected:hover:bg-swdc-gray-450",
  },
  gray: {
    tabUnderline: "bg-swdc-silver-400",
    buttonMobile: "bg-white text-swdc-blue-500 [&_svg]:text-swdc-black-600",
    tabMobile: "bg-white hover:bg-swdc-cool-gray-100",
    tabMobileActive:
      "data-selected:bg-swdc-silver-300 data-selected:hover:border-swdc-silver-200 data-selected:hover:bg-swdc-cool-gray-100",
  },
  white: {
    tabUnderline: "bg-swdc-orange-400",
    buttonMobile:
      "bg-swdc-cool-gray-100 text-swdc-blue-500 [&_svg]:text-swdc-black-600",
    tabMobile: "bg-swdc-cool-gray-100 hover:bg-white",
    tabMobileActive:
      "data-selected:bg-swdc-silver-300 data-selected:hover:border-swdc-silver-300 data-selected:hover:bg-white",
  },
};

type HorizontalTabNavigationProps = {
  tabClickOverride?: boolean;
};

export const HorizontalTabNavigation: FC<HorizontalTabNavigationProps> = ({
  tabClickOverride = false,
}) => {
  const { tabs, theme, activeTab, setActiveTab } = useTabContext();

  if (!tabs?.length) return null;

  const themeStyles = THEME_STYLES[theme || "gray"];

  return (
    <>
      <Listbox value={activeTab} onChange={setActiveTab}>
        <div className="relative block w-full md:hidden">
          <ListboxButton
            className={cn(
              "border-swdc-gray-200 sm:text-subhead-s text-subhead-2xs flex w-full min-w-0 cursor-pointer flex-row-reverse items-center justify-between border border-solid px-4 pt-3.5 pb-4 text-left leading-7 font-medium sm:leading-8",
              themeStyles.buttonMobile,
            )}
          >
            <CaretDownLine fill="currentColor" className="shrink-0" />
            <span className="mr-2 truncate overflow-hidden">
              {tabs[activeTab]?.label}
            </span>
          </ListboxButton>

          <ListboxOptions
            className={cn(
              "border-swdc-gray-200 text-body-m absolute inset-x-0 z-10 mt-3 -ml-2 w-[calc(100%+16px)] border border-solid font-medium shadow-[0_20px_40px_0_rgba(0,0,0,0.2)] focus:outline-none",
              themeStyles.tabMobile,
            )}
            as="ul"
          >
            {tabs?.map((tab, index) => (
              <ListboxOption
                data-linktype={tab.dlt}
                data-linkdetail={tab.dld}
                data-automation-id={`tab-${index}`}
                key={`${tab.label}-${index}`}
                as="li"
                className={cn(
                  "group data-focus:outline-swdc-blue-400 relative flex w-full cursor-pointer py-2 pr-12 pl-4 text-left not-data-focus:outline-none hover:outline-none focus:outline-none focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-selected:outline-none data-selected:hover:border-[3px] data-selected:hover:border-solid data-selected:hover:py-[0.3125rem] data-selected:hover:pr-[2.8125rem] data-selected:hover:pl-[0.8125rem]",
                  themeStyles.tabMobileActive,
                  themeStyles.tabMobile,
                )}
                value={index}
              >
                {({ selected }) => (
                  <span
                    className={`${selected ? "font-medium" : "font-normal"}`}
                  >
                    {selected && (
                      <CheckMarkThin
                        className="absolute top-1/2 right-4 w-5 -translate-y-1/2 group-data-selected:group-hover:right-[0.8125rem]"
                        fill="currentColor"
                      />
                    )}
                    {tab.label}
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      <TabGroup
        manual
        selectedIndex={activeTab}
        onChange={setActiveTab}
        as="span"
        className="hidden md:block"
      >
        <TabList
          as="ul"
          className="flex flex-wrap items-center gap-x-16 gap-y-4"
        >
          {tabs?.map((tab, index) => (
            <Tab
              data-linktype={tab.dlt}
              data-linkdetail={tab.dld}
              data-automation-group="tab"
              data-automation-id={`tab-${index}`}
              key={`${tab.label}-${index}`}
              className={cn(
                "group after:border-r-swdc-silver-300 data-focus:outline-swdc-blue-400 relative whitespace-nowrap after:absolute after:top-1/2 after:-right-8 after:h-6 after:-translate-y-1/2 after:border-r-2 last:after:border-r-0 focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 md:text-center lg:flex-none lg:text-left",
                index === activeTab
                  ? "text-headline"
                  : "text-link hover:text-link-hover active:text-link-pressed",
              )}
              as="li"
            >
              <div
                onClick={() => tabClickOverride && setActiveTab(index)}
                className={cn(
                  "text-link-m relative cursor-pointer gap-1 pb-1.5 font-bold",
                  index === activeTab
                    ? "text-headline"
                    : "text-link hover:text-link-hover active:text-link-pressed",
                )}
              >
                {tab.label}
                <div
                  className={cn(
                    themeStyles.tabUnderline,
                    "absolute right-0 -bottom-1 left-0 h-0 rounded-full group-hover:h-0.5",
                    index === activeTab
                      ? "h-1 group-hover:-bottom-1 group-hover:h-1"
                      : "mt-0.5",
                  )}
                ></div>
              </div>
            </Tab>
          ))}
        </TabList>
      </TabGroup>
    </>
  );
};
